import { isNil, isNotNil } from '@wistia/type-guards';
import type { ReactNode, RefObject } from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Menu } from '../Menu';

export type ContextMenuProps = {
  children: ReactNode;
  onRequestClose?: () => void;
  /**
   * If the menu is compact
   */
  compact?: boolean;
} & (
  | { position: { x: number; y: number }; side?: 'bottom' | 'top'; triggerRef?: never }
  | { position?: never; side?: never; triggerRef: RefObject<HTMLElement | null> }
);

/**
 * The ContextMenu is an extended implementation of the [Menu]() component that allows for right-click context menus.
 * It can be used in two ways:
 * 1. By providing a `triggerRef`, which will render the menu when the referenced element is right-clicked.
 * 2. By providing a `position` prop, which will render the menu at the specified coordinates.
 */
export const ContextMenu = ({
  position,
  triggerRef,
  children,
  side = 'bottom',
  onRequestClose = () => null,
  compact = false,
}: ContextMenuProps) => {
  const [isRightClicked, setIsRightClicked] = useState(false);
  const [menuPosition, setMenuPosition] = useState(position ?? { x: 0, y: 0 });

  useEffect(() => {
    if (isNil(position)) {
      const onContextMenu = (event: MouseEvent) => {
        event.preventDefault();
        setMenuPosition({ x: event.clientX, y: event.clientY });
        setIsRightClicked(true);
      };
      const triggerEl = triggerRef?.current;
      triggerEl?.addEventListener('contextmenu', onContextMenu);
      return () => {
        triggerEl?.removeEventListener('contextmenu', onContextMenu);
      };
    }

    return () => null;
  }, [position, triggerRef]);

  const isOpen = isNotNil(position) || isRightClicked;

  if (!isOpen) {
    return null;
  }

  const menu = (
    <Menu
      compact={compact}
      isOpen={isOpen}
      onOpenChange={(next) => {
        setIsRightClicked(next);
        if (!next) {
          onRequestClose();
        }
      }}
      side={side}
      trigger={
        <button
          aria-label="context menu"
          style={{
            width: '1px',
            height: '1px',
            all: 'unset',
            position: 'fixed',
            top: menuPosition.y,
            left: menuPosition.x,
          }}
          type="button"
        />
      }
    >
      {children}
    </Menu>
  );

  // We portal the menu into document body here to ensure that the fixed
  // position element's top and left are relative to the viewport, not the
  // trigger element. The trigger element could have a css transform on it which
  // mucks even fixed position children, apparently.
  if (isNotNil(triggerRef)) {
    return createPortal(menu, document.body);
  }

  return menu;
};
